import {useEffect, useState} from 'react'
import ThumbIcon from '../svg-component/ThumbIcon'
import {upsertTicketFeedbackApi} from 'utils/api/auth/ticketService'
import {useTranslation} from 'react-i18next'
import ReasonModal from './ReasonModal'

type Props = {
 ticketNumber: string
 feedbackStatus: 'SATISFIED' | 'NOT_SATISFIED' | null
 user_feedback_comment?: string | null
}

const getInitialFeedback = (
 status: Props['feedbackStatus']
): 'like' | 'dislike' | null => {
 if (status === 'SATISFIED') return 'like'
 if (status === 'NOT_SATISFIED') return 'dislike'
 return null
}

const ResolutionFeedback = ({
 ticketNumber,
 feedbackStatus,
 user_feedback_comment,
}: Props) => {
 const {t} = useTranslation()

 const [feedback, setFeedback] = useState<
  'like' | 'dislike' | null
 >(getInitialFeedback(feedbackStatus))

 const [reason, setReason] = useState<string | null>(
  user_feedback_comment || null
 )

 const [loading, setLoading] = useState(false)
 const [reasonModalOpen, setReasonModalOpen] =
  useState(false)

 const submitFeedback = async (
  type: 'like' | 'dislike'
 ) => {
  if (loading) return

  if (type === 'dislike') {
   setReasonModalOpen(true)
   return
  }
  try {
   setLoading(true)
   await upsertTicketFeedbackApi({
    ticket_number: ticketNumber,
    feedback_status: 'SATISFIED',
   })

   setFeedback('like')
   setReason(null)
  } finally {
   setLoading(false)
  }
 }

 const handleSubmit = async (comment: string) => {
  try {
   setLoading(true)

   await upsertTicketFeedbackApi({
    ticket_number: ticketNumber,
    feedback_status: 'NOT_SATISFIED',
    user_comment: comment,
   })

   setFeedback('dislike')
   setReason(comment)
   setReasonModalOpen(false)
  } finally {
   setLoading(false)
  }
 }

 useEffect(() => {
  setFeedback(getInitialFeedback(feedbackStatus))
  setReason(user_feedback_comment || null)
 }, [feedbackStatus, user_feedback_comment])

 return (
  <div className='mt-6'>
   {feedback ? (
    <div className='flex items-start gap-2 text-sm'>
     {feedback === 'like' ? (
      <div className='flex items-center gap-2 text-green-600 font-medium'>
       <ThumbIcon active color='#53A600' />
       <span>Satisfied</span>
      </div>
     ) : (
      <div className='flex flex-col gap-1'>
       <div className='flex items-center gap-2 text-red-600 font-medium'>
        <ThumbIcon active color='#CC293F' rotate />
        <span>Not Satisfied</span>
       </div>

       {reason && (
        <span className='text-gray-500 text-xs'>
         {reason}
        </span>
       )}
      </div>
     )}
    </div>
   ) : (
    <div className='flex items-center gap-3 text-sm text-gray-700'>
     <span>
      {t('ticket.resolutionFeedback.satisfiedLabel')}
     </span>

     <button
      disabled={loading}
      onClick={() => {
       void submitFeedback('like')
      }}
      className='p-1 disabled:opacity-50'
     >
      <ThumbIcon color='#53A600' />
     </button>

     <button
      disabled={loading}
      onClick={() => {
       void submitFeedback('dislike')
      }}
      className='p-1 disabled:opacity-50'
     >
      <ThumbIcon color='#CC293F' rotate />
     </button>
    </div>
   )}

   <ReasonModal
    open={reasonModalOpen}
    onClose={() => setReasonModalOpen(false)}
    onSubmit={handleSubmit}
    title='Reason for Not Satisfied'
    placeholder='Enter comment'
    submitText='Submit'
   />
  </div>
 )
}

export default ResolutionFeedback
